import { tryCatch } from '@bwoty-web/utils';
import { ACTION_TYPES } from '../constants/actionTypes/results';
import { fetchTotalHits } from '../../service/finderTotalHits';
import { fetchResults } from '../../services/results';
import { syncStateToQueryParams } from '../../utils/urlStateUtils';
import { resultTypes } from '../../appSettings';
import { logToDataLayer } from '../../utils/tracking';

export const setTotalHits = (total) => {
  return {
    type: ACTION_TYPES.SET_RESULTS_COUNT_TOTAL,
    payload: {
      total,
    },
  };
};

export const getTotalHits = () => {
  return async (dispatch, getState) => {
    const { siteSettings } = getState();
    const { filters, siteId, baseUrl } = siteSettings;
    try {
      const totalHits = await fetchTotalHits({ baseUrl, siteId, filters });
      dispatch(setTotalHits(totalHits));
    } catch (err) {
      console.error(err);
    }
  };
};

export const setListCount = (listCount) => {
  syncStateToQueryParams({
    listCount,
  });

  return {
    type: ACTION_TYPES.SET_LIST_COUNT,
    payload: {
      listCount,
    },
  };
};

export const addSerializedComponent = (serializedComponent) => {
  return {
    type: ACTION_TYPES.SET_SERIALIZED_COMPONENT,
    payload: {
      serializedComponent,
    },
  };
};

export const handleResultHeaderSelect = (selectedResultHeader) => {
  syncStateToQueryParams({
    srh: selectedResultHeader,
  });

  return (dispatch) => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_RESULT_HEADER,
      payload: {
        selectedResultHeader,
      },
    });
    logToDataLayer({
      event: 'vacationfinder',
      eventAction: 'Click tab',
      eventLabel: selectedResultHeader,
    });
  };
};

export const getHotels = () => {
  return async (dispatch, getState) => {
    const { siteSettings } = getState();
    const { baseUrl, siteId, filters } = siteSettings;
    dispatch({ type: ACTION_TYPES.GET_RESULT_HOTELS });

    const [error, data] = await tryCatch(async () => {
      const response = await fetchResults({
        resultType: resultTypes.HOTELS,
        baseUrl,
        siteId,
        filters,
      });
      return response;
    });

    if (error) {
      console.error('Failed to get hotels', error);
    }
    if (data) {
      const { hotels, hits } = data;
      dispatch({
        type: ACTION_TYPES.GET_RESULT_HOTELS_SUCCESS,
        payload: {
          hotels,
          hits,
        },
      });
    } else {
      dispatch({ type: ACTION_TYPES.GET_RESULT_HOTELS_ERROR });
    }
  };
};

export const getDestinations = () => {
  return async (dispatch, getState) => {
    const { siteSettings } = getState();
    const { baseUrl, siteId, filters } = siteSettings;
    dispatch({ type: ACTION_TYPES.GET_RESULT_DESTINATIONS });

    const [error, data] = await tryCatch(async () => {
      const response = await fetchResults({
        resultType: resultTypes.DESTINATIONS,
        baseUrl,
        siteId,
        filters,
      });
      return response;
    });

    if (error) {
      console.error('Failed to get destinations', error);
    }

    if (data) {
      const { destinations, hits } = data;
      dispatch({
        type: ACTION_TYPES.GET_RESULT_DESTINATIONS_SUCCESS,
        payload: {
          destinations,
          hits,
        },
      });
    } else {
      dispatch({ type: ACTION_TYPES.GET_RESULT_DESTINATIONS_ERROR });
    }
  };
};

export const getExcursions = () => {
  return async (dispatch, getState) => {
    const { siteSettings } = getState();
    const { baseUrl, siteId, filters } = siteSettings;
    dispatch({ type: ACTION_TYPES.GET_RESULT_EXCURSIONS });

    const [error, data] = await tryCatch(async () => {
      const response = await fetchResults({
        resultType: resultTypes.EXCURSIONS,
        baseUrl,
        siteId,
        filters,
      });
      return response;
    });

    if (error) {
      console.error('Failed to get excursions', error);
    }

    if (data) {
      const { excursions, hits } = data;
      dispatch({
        type: ACTION_TYPES.GET_RESULT_EXCURSIONS_SUCCESS,
        payload: {
          excursions,
          hits,
        },
      });
    } else {
      dispatch({ type: ACTION_TYPES.GET_RESULT_EXCURSIONS_ERROR });
    }
  };
};

export const getGroupTrips = () => {
  return async (dispatch, getState) => {
    const { siteSettings } = getState();
    const { baseUrl, siteId, filters } = siteSettings;
    dispatch({ type: ACTION_TYPES.GET_RESULT_GROUPTRIPS });

    const [error, data] = await tryCatch(async () => {
      const response = await fetchResults({
        resultType: resultTypes.GROUP_TRIPS,
        baseUrl,
        siteId,
        filters,
      });
      return response;
    });

    if (error) {
      console.error('Failed to get group trips', error);
    }

    if (data) {
      const { groupTrips, hits } = data;
      dispatch({
        type: ACTION_TYPES.GET_RESULT_GROUP_TRIPS_SUCCESS,
        payload: {
          groupTrips,
          hits,
        },
      });
    } else {
      dispatch({ type: ACTION_TYPES.GET_RESULT_GROUP_TRIPS_ERROR });
    }
  };
};

export const getAll = () => {
  return async (dispatch, getState) => {
    const { siteSettings } = getState();
    const { baseUrl, siteId, filters } = siteSettings;
    dispatch({ type: ACTION_TYPES.GET_RESULT_ALL });

    const [error, data] = await tryCatch(async () => {
      const response = await fetchResults({
        resultType: resultTypes.ALL,
        baseUrl,
        siteId,
        filters,
      });
      return response;
    });

    if (error) {
      console.error('Failed to get all result data', error);
    }

    if (data) {
      const [
        hotelsResp,
        destinationsResp,
        excursionsResp,
        groupTripsResp,
        articlesResp,
      ] = data;
      const totalHits =
        hotelsResp.hits +
        destinationsResp.hits +
        excursionsResp.hits +
        groupTripsResp.hits;

      dispatch({
        type: ACTION_TYPES.GET_RESULT_ALL_SUCCESS,
        payload: {
          [resultTypes.HOTELS]: hotelsResp[resultTypes.HOTELS],
          [resultTypes.DESTINATIONS]:
            destinationsResp[resultTypes.DESTINATIONS],
          [resultTypes.EXCURSIONS]: excursionsResp[resultTypes.EXCURSIONS],
          [resultTypes.GROUP_TRIPS]: groupTripsResp[resultTypes.GROUP_TRIPS],
          [resultTypes.ARTICLES]: articlesResp[resultTypes.ARTICLES],
          totalHits,
          hotelHits: hotelsResp.hits,
          destinationHits: destinationsResp.hits,
          excursionHits: excursionsResp.hits,
          groupTripHits: groupTripsResp.hits,
          articleHits: articlesResp.hits,
        },
      });
    } else {
      dispatch({ type: ACTION_TYPES.GET_RESULT_ALL_ERROR });
    }
  };
};

export const setNeverShowReminder = () => {
  return {
    type: ACTION_TYPES.SET_NEVER_SHOW_REMINDER,
  };
};
